import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinRoom: (room: 'band' | 'audience' | 'admin') => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const joinRoom = useCallback((room: 'band' | 'audience' | 'admin') => {
    if (socket) {
      socket.emit(`join:${room}`);
    }
  }, [socket]);

  return (
    <SocketContext.Provider value={{ socket, isConnected, joinRoom }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// Custom hooks for specific socket events

export const useQueueUpdates = (callback: (data: any) => void) => {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on('queue:update', callback);

    return () => {
      socket.off('queue:update', callback);
    };
  }, [socket, callback]);
};

export const useNowPlaying = (callback: (data: any) => void) => {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on('now-playing:update', callback);

    return () => {
      socket.off('now-playing:update', callback);
    };
  }, [socket, callback]);
};

export const useSongDone = (callback: (data: any) => void) => {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on('song:done', callback);

    return () => {
      socket.off('song:done', callback);
    };
  }, [socket, callback]);
};

export const useGigEvents = (
  onStart?: (data: any) => void,
  onEnd?: (data: any) => void
) => {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    if (onStart) {
      socket.on('gig:started', onStart);
    }
    if (onEnd) {
      socket.on('gig:ended', onEnd);
    }

    return () => {
      if (onStart) socket.off('gig:started', onStart);
      if (onEnd) socket.off('gig:ended', onEnd);
    };
  }, [socket, onStart, onEnd]);
};

export const useVoteReceived = (callback: (data: any) => void) => {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on('vote:received', callback);

    return () => {
      socket.off('vote:received', callback);
    };
  }, [socket, callback]);
};
